/**
 * Cancelar Cobro de Matrícula — migración web de ares6040.
 *
 * Lista los cobros vigentes de pagomatri (criterio chasis/nombre o rango de
 * fechas, excluyentes) y permite anularlos con razón obligatoria (valor=0 +
 * razón + producto.matestado='') o reimprimir el recibo (en el escritorio la
 * reimpresión crasheaba por un argumento faltante).
 * Cancelar requiere el permiso `borrar` de ares6040 (lo valida el backend).
 * "Imprimir PDF" saca el listado filtrado (con su total) en vista previa.
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import { Page, Text, View, Document, PDFViewer } from "@react-pdf/renderer";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";
import { useMultiempresa, SelectorEmpresa, nombreEmpresa } from "lib/multiempresa";
import { imprimirReciboMatricula } from "modules/matriculas/cobromatricula";
import { labelTipo } from "lib/documentosvehiculo";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

const NAVY = "#18586b";

const fmtFechaDMY = (iso) => {
  const [a, m, d] = String(iso || "").slice(0, 10).split("-");
  return a && m && d ? `${d}/${m}/${a}` : "";
};

// Listado de cobros en PDF (mismo patrón visual del informe ares6020).
function ListadoCobrosPdf({ rows, nombreNegocio, subtitulo, conEmpresa, empresas, isXs }) {
  const cols = [
    { h: "Prest#", w: 44, get: (r) => r.noprest },
    { h: "Fecha", w: 58, get: (r) => r.fecha },
    ...(conEmpresa
      ? [{ h: "Empresa", w: 70, get: (r) => nombreEmpresa(empresas, r.empresa || 1) }]
      : []),
    { h: "Cliente", flex: 1.4, get: (r) => r.nombres },
    { h: "Cédula", w: 74, get: (r) => r.cedula },
    { h: "Vehículo", flex: 1, get: (r) => [r.marca, r.modelo].filter(Boolean).join(" ") },
    { h: "Chasis", w: 104, get: (r) => r.chasis },
    {
      h: "Documento",
      w: 62,
      get: (r) =>
        (r.tipodocumento ? labelTipo(r.tipodocumento) : "") + (r.financiado ? " (fin.)" : ""),
    },
    { h: "Valor", w: 60, right: true, get: (r) => fmtMoney(r.valor) },
  ];
  const total = rows.reduce(
    (sum, r) => sum + (parseFloat(String(r.valor || "").replace(/,/g, "")) || 0),
    0
  );
  const anchoCol = (c) => (c.flex ? { flex: c.flex } : { width: c.w });
  return (
    <PDFViewer
      style={{
        height: isXs ? "calc(100vh - 120px)" : "550px",
        width: "100%",
        colorScheme: "light",
        background: "#fff",
      }}
    >
      <Document>
        <Page
          size="LETTER"
          orientation="portrait"
          wrap
          style={{ backgroundColor: "#FFFFFF", padding: 24, paddingBottom: 48 }}
        >
          <View style={{ textAlign: "center", fontSize: 12, textTransform: "uppercase" }} fixed>
            <Text>{nombreNegocio || ""}</Text>
          </View>
          <View style={{ textAlign: "center", fontSize: 9, textTransform: "uppercase" }} fixed>
            <Text>Cobros de Matrícula</Text>
          </View>
          <View style={{ textAlign: "center", fontSize: 8, color: "#475569", marginBottom: 10 }} fixed>
            <Text>{subtitulo}</Text>
          </View>
          <View
            style={{
              flexDirection: "row",
              fontSize: 8,
              backgroundColor: NAVY,
              color: "#FFFFFF",
              paddingVertical: 4,
            }}
            fixed
          >
            {cols.map((c) => (
              <Text
                key={c.h}
                style={{
                  padding: 4,
                  fontWeight: "bold",
                  ...anchoCol(c),
                  textAlign: c.right ? "right" : "left",
                }}
              >
                {c.h}
              </Text>
            ))}
          </View>
          {rows.map((r, i) => (
            <View
              key={r.id ?? i}
              style={{
                flexDirection: "row",
                fontSize: 8,
                color: "#1a2430",
                paddingVertical: 3,
                backgroundColor: i % 2 === 0 ? "#f5f8fa" : "#FFFFFF",
              }}
              wrap={false}
            >
              {cols.map((c) => (
                <Text
                  key={c.h}
                  style={{ padding: 4, ...anchoCol(c), textAlign: c.right ? "right" : "left" }}
                >
                  {c.get(r)}
                </Text>
              ))}
            </View>
          ))}
          <View
            style={{
              flexDirection: "row",
              fontSize: 9,
              fontWeight: "bold",
              color: "#1a2430",
              borderTopWidth: 2,
              borderTopColor: NAVY,
              paddingVertical: 5,
              marginTop: 2,
            }}
          >
            <Text style={{ padding: 4, flex: 1 }}>Total — {rows.length} cobros</Text>
            <Text style={{ padding: 4, width: 90, textAlign: "right" }}>{fmtMoney(total)}</Text>
          </View>
          <Text
            style={{ position: "absolute", bottom: 20, right: 24, fontSize: 7, color: "#64748B" }}
            render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`}
            fixed
          />
        </Page>
      </Document>
    </PDFViewer>
  );
}

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin cobros vigentes en el rango
      </Typography>
    </Stack>
  );
}

export default function CancelarCobroMatricula({ embedded = false, refreshToken = 0 } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const [controller] = useMaterialUIController();
  const { nombreNegocio } = controller;
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const [pdfOpen, setPdfOpen] = useState(false);
  // Filtros con que se cargó el listado, para el subtítulo del PDF (los
  // campos pueden haber cambiado sin volver a procesar).
  const [filtroCargado, setFiltroCargado] = useState(null);

  const [criterio, setCriterio] = useState("");
  const [fecha1, setFecha1] = useState(inicioMesISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // Multi-empresa: filtro y columna (solo con el modo activo)
  const multiempresa = useMultiempresa();
  const [empresaFiltro, setEmpresaFiltro] = useState("0");

  const cargar = async () => {
    setLoading(true);
    try {
      const { ok, data } = await post("/api/cobromatricula_listar", {
        criterio: criterio.trim(),
        fechadesde: fecha1,
        fechahasta: fecha2,
        ...(multiempresa.activo ? { empresa: Number(empresaFiltro) } : {}),
      });
      if (ok) {
        setRows(data.cobros || []);
        setFiltroCargado({
          criterio: criterio.trim(),
          fecha1,
          fecha2,
          empresa: multiempresa.activo ? empresaFiltro : "0",
        });
      }
      else showAlert({ message: data.Error || "No se pudieron listar los cobros", color: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  // ---- Cancelación ----
  const [target, setTarget] = useState(null);
  const [razon, setRazon] = useState("");
  const [cancelando, setCancelando] = useState(false);

  const cancelar = async () => {
    if (!razon.trim()) {
      showAlert({
        message: "La razón de la cancelación no puede estar en blanco",
        color: "warning",
      });
      return;
    }
    setCancelando(true);
    try {
      const { ok, data } = await post("/api/cancelarmatricula", {
        id: target.id,
        razon: razon.trim(),
      });
      if (ok) {
        showAlert({ message: data.ok || "Pago de matrícula cancelado", color: "success" });
        setTarget(null);
        setRazon("");
        cargar();
      } else {
        showAlert({ message: data.Error || "No se pudo cancelar", color: "error" });
      }
    } finally {
      setCancelando(false);
    }
  };

  const reimprimir = (row) => {
    imprimirReciboMatricula({
      id: row.id,
      fecha: row.fecha,
      cedula: row.cedula,
      cliente: row.nombres,
      noprest: row.noprest,
      chasis: row.chasis,
      marca: row.marca,
      modelo: row.modelo,
      colore: row.colore,
      valor: row.valor,
      razon: row.razon,
      documento: row.tipodocumento,
      compania: localStorage.getItem("nombrenegocio") || "",
    });
  };

  const columns = [
    { field: "noprest", headerName: "Préstamo", width: 95 },
    { field: "fecha", headerName: "Fecha", width: 105 },
    // Multi-empresa: empresa del cobro (solo con el modo activo)
    ...(multiempresa.activo
      ? [
          {
            field: "empresa",
            headerName: "Empresa",
            width: 130,
            valueGetter: (value, row) => nombreEmpresa(multiempresa.empresas, row.empresa || 1),
          },
        ]
      : []),
    { field: "cedula", headerName: "Cédula", width: 135 },
    { field: "nombres", headerName: "Cliente", flex: 1, minWidth: 170 },
    { field: "chasis", headerName: "Chasis", flex: 1, minWidth: 200 },
    {
      field: "vehiculo",
      headerName: "Vehículo",
      width: 150,
      valueGetter: (_v, row) => [row.marca, row.modelo].filter(Boolean).join(" "),
    },
    {
      field: "tipodocumento",
      headerName: "Documento",
      width: 130,
      valueFormatter: (value) => (value ? labelTipo(value) : "—"),
    },
    {
      field: "valor",
      headerName: "Valor",
      width: 150,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Stack direction="row" spacing={0.6} alignItems="center" justifyContent="flex-end">
          {params.row.financiado && (
            <Chip
              size="small"
              label="Financiada"
              sx={{ bgcolor: "#FFF3E0", color: "#B45309", fontWeight: 700, height: 20 }}
            />
          )}
          <span>{fmtMoney(params.value)}</span>
        </Stack>
      ),
    },
    {
      field: "acciones",
      headerName: "",
      width: 230,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.8}>
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => {
              setRazon("");
              setTarget(params.row);
            }}
            sx={btnOutline(FP.color.danger)}
          >
            Cancelar
          </Button>
          <Button
            size="small"
            startIcon={<PrintOutlinedIcon />}
            onClick={() => reimprimir(params.row)}
            sx={btnOutline(FP.color.navy)}
          >
            Imprimir
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box component={embedded ? "section" : "main"} sx={embedded ? {} : { minHeight: "100vh", bgcolor: FP.color.surface }}>
      {!embedded && (
        <PageHeader
          title="Cancelar Cobro de Matrícula"
          subtitle="Anula cobros de matrícula vigentes — requiere permiso de borrado"
        />
      )}
      <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="Buscar por chasis o nombre"
                placeholder="Dejar vacío para buscar por fechas"
                value={criterio}
                onChange={(e) => setCriterio(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                disabled={!!criterio.trim()}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                disabled={!!criterio.trim()}
                sx={fpInputSx}
              />
            </Grid>
            {multiempresa.activo && (
              <Grid item xs={6} sm={3} md={2}>
                <SelectorEmpresa
                  value={empresaFiltro}
                  onChange={setEmpresaFiltro}
                  empresas={multiempresa.empresas}
                  sx={fpInputSx}
                />
              </Grid>
            )}
            <Grid item xs={6} sm={2} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={cargar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
            <Grid
              item
              xs={6}
              sm={12}
              md={3}
              sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.5 }}
            >
              <Button
                startIcon={<PictureAsPdfIcon />}
                onClick={() => setPdfOpen(true)}
                disabled={loading || rows.length === 0}
                sx={btnOutline(FP.color.navy)}
              >
                Imprimir PDF
              </Button>
              <Typography
                sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: "0.85rem" }}
              >
                {rows.length} cobros
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              loading={loading}
              rowHeight={46}
              pageSizeOptions={[25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>

      {/* Listado en PDF */}
      {pdfOpen && (
        <Dialog
          open={pdfOpen}
          maxWidth="lg"
          fullWidth
          fullScreen={isXs}
          onClose={() => setPdfOpen(false)}
          PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
        >
          <DialogTitle
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <Typography variant="h6" fontWeight="bold">
              Cobros de Matrícula — PDF
            </Typography>
            <Button onClick={() => setPdfOpen(false)} sx={{ minWidth: "auto", p: 1 }}>
              <CancelIcon />
            </Button>
          </DialogTitle>
          <DialogContent>
            <ListadoCobrosPdf
              rows={rows}
              nombreNegocio={nombreNegocio}
              isXs={isXs}
              conEmpresa={multiempresa.activo}
              empresas={multiempresa.empresas}
              subtitulo={(() => {
                const f = filtroCargado || {};
                const partes = [
                  f.criterio
                    ? `Búsqueda: ${f.criterio}`
                    : `Del ${fmtFechaDMY(f.fecha1)} al ${fmtFechaDMY(f.fecha2)}`,
                ];
                if (multiempresa.activo && f.empresa && f.empresa !== "0") {
                  partes.push(`Empresa: ${nombreEmpresa(multiempresa.empresas, Number(f.empresa))}`);
                }
                return partes.join(" · ");
              })()}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Cancelación con razón */}
      <Dialog
        open={!!target}
        onClose={() => setTarget(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.danger }}>
          Cancelar cobro de matrícula
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem", mb: 2 }}>
            Chasis <b>{target?.chasis}</b> de <b>{target?.nombres}</b> por RD${" "}
            <b>{fmtMoney(target?.valor)}</b>. El cobro quedará anulado.
          </DialogContentText>
          {target?.financiado && (
            <DialogContentText sx={{ fontSize: "0.85rem", mb: 2, color: "#B45309" }}>
              Este cobro está financiado en las cuotas del préstamo: al anularlo se
              revertirá la distribución. No es posible si alguna de las cuotas
              afectadas ya tiene pagos.
            </DialogContentText>
          )}
          <TextField
            fullWidth
            label="Razón de la cancelación"
            value={razon}
            onChange={(e) => setRazon(e.target.value)}
            sx={fpInputSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTarget(null)} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              cancelando ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />
            }
            onClick={cancelar}
            disabled={cancelando}
            sx={btnSolid("#D32F2F", "#B71C1C")}
          >
            Sí, cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
